import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type MeetingMessage from 'owa-service/lib/contract/MeetingMessage';
import { isMeetingDelegated } from './isMeetingDelegated';

function isEventPollItemClass(itemClass: string | undefined): boolean {
    return !!itemClass?.match(/^ipm\.(note\.)?schedule\.meeting\.eventpoll\.(request|canceled)/i);
}

export function isEventPollRequest(itemClass: string | undefined): boolean {
    return isGlobalFeatureEnabled('rp-event-poll-calendar-card') && isEventPollItemClass(itemClass);
}

const shareAvailabilityCardIdPrefix = 'ShareAvailabilityContainer-';

/** The parsed actionable-message cards loaded onto a mail item. */
interface MessageWithAdaptiveCardData extends MeetingMessage {
    AdaptiveCardData?: { cardDetails?: { card?: unknown }[] };
}

function isAllPollsShareAvailabilityEnabled(): boolean {
    return isGlobalFeatureEnabled('cal-share-availability-email');
}

/**
 * True when the item class could use the availability presentation: either every event poll is
 * converted, or only polls whose card carries the Share Availability marker.
 */
export function isShareAvailabilityEmail(itemClass: string | undefined): boolean {
    return (
        (isAllPollsShareAvailabilityEnabled() ||
            isGlobalFeatureEnabled('cal-share-availability-email-v3')) &&
        isEventPollItemClass(itemClass)
    );
}

/**
 * Share Availability (version 3) poll cards mark their first top-level container with the id
 * `ShareAvailabilityContainer-<pollId>`. Returns the opaque poll id independently of flights.
 */
export function getShareAvailabilityCardPollId(card: unknown): string | undefined {
    const firstElement = (card as { body?: { type?: unknown; id?: unknown }[] } | undefined)
        ?.body?.[0];
    if (
        firstElement?.type === 'Container' &&
        typeof firstElement.id === 'string' &&
        firstElement.id.length > shareAvailabilityCardIdPrefix.length &&
        firstElement.id.startsWith(shareAvailabilityCardIdPrefix)
    ) {
        return firstElement.id.slice(shareAvailabilityCardIdPrefix.length);
    }
    return undefined;
}

function getShareAvailabilityPollId(message: MessageWithAdaptiveCardData): string | undefined {
    for (const { card } of message.AdaptiveCardData?.cardDetails ?? []) {
        const pollId = getShareAvailabilityCardPollId(card);
        if (pollId) {
            return pollId;
        }
    }
    return undefined;
}

function hasShareAvailabilityCardMarker(message: MessageWithAdaptiveCardData): boolean {
    return !!getShareAvailabilityPollId(message);
}

/**
 * Poll id of a Share Availability poll delivered as a regular message rather than an event poll
 * meeting item, so there is no calendar event to load it from.
 */
export function getShareAvailabilityMessagePollId(
    message: MessageWithAdaptiveCardData | undefined,
    mailboxInfo: { type: string } | undefined
): string | undefined {
    if (
        !message ||
        !isGlobalFeatureEnabled('cal-share-availability-email-v3') ||
        isEventPollItemClass(message.ItemClass) ||
        message.IsDraft ||
        mailboxInfo?.type !== 'UserMailbox'
    ) {
        return undefined;
    }
    return getShareAvailabilityPollId(message);
}

/**
 * Only received, non-delegated event polls in the user's own mailbox can use the availability
 * presentation. This does not depend on the asynchronously loaded poll card.
 */
function isShareAvailabilityEmailCandidate(
    message: MessageWithAdaptiveCardData | undefined,
    mailboxInfo: { type: string } | undefined
): message is MessageWithAdaptiveCardData {
    return (
        !!message &&
        isShareAvailabilityEmail(message.ItemClass) &&
        !message.IsDraft &&
        mailboxInfo?.type === 'UserMailbox' &&
        !isMeetingDelegated(message)
    );
}

/**
 * Item-level share-availability presentation: an eligible poll whose loaded card is marked as a
 * Share Availability poll, unless every poll is converted.
 */
export function isShareAvailabilityEmailMessage(
    message: MessageWithAdaptiveCardData | undefined,
    mailboxInfo: { type: string } | undefined
): boolean {
    return (
        isShareAvailabilityEmailCandidate(message, mailboxInfo) &&
        (isAllPollsShareAvailabilityEnabled() || hasShareAvailabilityCardMarker(message))
    );
}

/**
 * True when the message should use the event-poll card path, either through the legacy event
 * poll flight or as a share-availability candidate. Candidates route through the poll path
 * before their card loads, so the legacy poll drawer is ready if availability is not shown.
 */
export function isEventPollMessage(
    message: MessageWithAdaptiveCardData | undefined,
    mailboxInfo: { type: string } | undefined
): boolean {
    return (
        isEventPollRequest(message?.ItemClass) ||
        isShareAvailabilityEmailCandidate(message, mailboxInfo)
    );
}
